<template>
  <div ref="rootRef" class="rich-html-case rich-html-case--intelligentPetFeeder">
<!-- HERO -->
<section class="hero">
  <div class="container">
    <div class="hero-inner">
      <div class="hero-content">
        <div class="hero-tag">🐾 Case Study · 智能宠物喂食器 APP</div>
        <h1 class="hero-title">DownyPaws<br><span>智能宠物喂食器</span></h1>
        <p class="hero-subtitle">让每一餐都精准可控 — 跨平台智能喂食管理</p>
        <p class="hero-desc">以宠物健康为核心，将智能硬件、定时喂食计划与干燥剂管理融为一体。无论你在哪里，随时掌控宠物的每一餐。</p>
        <div class="hero-stats">
          <div><div class="hero-stat-val">50+</div><div class="hero-stat-label">可配置喂食参数</div></div>
          <div><div class="hero-stat-val">3</div><div class="hero-stat-label">跨平台支持</div></div>
          <div><div class="hero-stat-val">7×24</div><div class="hero-stat-label">实时设备监控</div></div>
        </div>
        <div class="platforms">
          <div class="platform-badge">📱 iOS</div>
          <div class="platform-badge">🤖 Android</div>
          <div class="platform-badge">🔵 HarmonyOS</div>
        </div>
      </div>
      <div class="hero-phone-area">
        <div class="phone-wrap secondary">
          <img
            class="hero-phone-img"
            :src="getImage('device-basic.jpg')"
            alt="设备详情"
            @error="onImageError"
          />
        </div>
        <div class="phone-wrap">
          <img
            class="hero-phone-img"
            :src="getImage('home.jpg')"
            alt="首页总览"
            @error="onImageError"
          />
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Project Introduction -->
<section class="project-intro">
  <div class="project-intro__inner">
    <img class="project-intro__title-img" :src="introduceText" alt="项目介绍" />
    <p class="project-intro__text fontRegular">{{ projectIntroduction }}</p>
  </div>
</section>

<div class="page-detail">
<!-- BACKGROUND -->
<section class="background-section">
  <div class="container">
    <div class="section-header">
      <div class="section-badge">📋 项目背景</div>
      <h2 class="section-title">为什么需要 DownyPaws 智能宠物喂食器？</h2>
      <p class="section-sub">上班族宠物主人的喂食焦虑，催生了这款让宠物定时吃饭、主人安心上班的智能解决方案。</p>
    </div>
    <div class="bg-grid">
      <div class="bg-card">
        <div class="bg-card-icon">😟</div>
        <div class="bg-card-title">用户痛点</div>
        <div class="bg-card-text">上班族宠物主人每天都面临"我的猫今天吃了吗"的焦虑；出差期间无法按时喂食；喂食过多或过少导致宠物健康问题；没有设备状态监控，缺粮了也浑然不觉。</div>
      </div>
      <div class="bg-card">
        <div class="bg-card-icon">🎯</div>
        <div class="bg-card-title">设计目标</div>
        <div class="bg-card-text">打造一款对宠物主人友好的 App：操作直觉化、设备状态一目了然、喂食计划灵活可配。同时支持多设备管理与干燥剂智能追踪，让每台喂食器的状态尽在掌握。</div>
      </div>
      <div class="bg-card">
        <div class="bg-card-icon">⚙️</div>
        <div class="bg-card-title">技术方案</div>
        <div class="bg-card-text">采用混合 App 开发，同时覆盖 iOS、Android 和鸿蒙三大平台。通过蓝牙配网 + Wi-Fi 2.4GHz 连接智能喂食硬件，实现远程投喂与实时状态推送。</div>
      </div>
      <div class="bg-card">
        <div class="bg-card-icon">📱</div>
        <div class="bg-card-title">产品定位</div>
        <div class="bg-card-text">定位「智能喂食管家」，以喂食控制为核心，延伸至干燥剂管理与多设备协同。品牌名 DownyPaws 智能宠物喂食器，体现专业感与功能定位，聚焦国内宠物市场。</div>
      </div>
    </div>
  </div>
</section>

<!-- UI SHOWCASE -->
<section class="showcase-section">
  <div class="container">
    <div class="section-header">
      <div class="section-badge">🎨 界面展示</div>
      <h2 class="section-title">核心功能界面</h2>
      <p class="section-sub">涵盖首页、设备详情、干燥剂设置、设备共享等关键模块的完整 UI 设计。</p>
    </div>
    <div class="showcase-grid">
      <!-- Screen 1: Home -->
      <div>
        <div class="showcase-phone">
          <div class="showcase-phone-inner">
            <img style="width:100%;height:100%;object-fit:cover;object-position:center;display:block;" alt="首页总览" :src="getImage('home.jpg')" @error="onImageError" />
          </div>
        </div>
        <div class="showcase-label">首页总览</div>
      </div>

      <!-- Screen 2: Device Detail -->
      <div>
        <div class="showcase-phone">
          <div class="showcase-phone-inner">
            <img style="width:100%;height:100%;object-fit:cover;object-position:center;display:block;" alt="设备详情（无摄像头版）" :src="getImage('device-basic.jpg')" @error="onImageError" />
          </div>
        </div>
        <div class="showcase-label">设备详情</div>
      </div>

      <!-- Screen 3: Pet Profile -->
      <div>
        <div class="showcase-phone">
          <div class="showcase-phone-inner">
            <img style="width:100%;height:100%;object-fit:cover;object-position:center;display:block;" alt="干燥剂设置" :src="getImage('desiccant-settings.jpg')" @error="onImageError" />
          </div>
        </div>
        <div class="showcase-label">干燥剂设置</div>
      </div>

      <!-- Screen 4: Notifications -->
      <div>
        <div class="showcase-phone">
          <div class="showcase-phone-inner">
            <img style="width:100%;height:100%;object-fit:cover;object-position:center;display:block;" alt="设备共享" :src="getImage('share-family.jpg')" @error="onImageError" />
          </div>
        </div>
        <div class="showcase-label">设备共享</div>
      </div>
    </div>
  </div>
</section>

<!-- FEATURES -->
<section class="features-section">
  <div class="container">
    <div class="section-header">
      <div class="section-badge">⚡ 核心功能</div>
      <h2 class="section-title">功能亮点</h2>
      <p class="section-sub">三大核心模块，覆盖宠物喂食全场景需求。</p>
    </div>
    <div class="features-grid">
      <div class="feature-card">
        <div class="feature-icon">🍽️</div>
        <div class="feature-title">智能喂食控制</div>
        <div class="feature-text">支持远程手动投喂与定时自动喂食。可配置多餐计划，精确到每次的份量与时间，确保宠物规律进食不过量。喂食完成即时推送确认通知。</div>
        <div class="feature-tags"><span class="feature-tag">远程投喂</span><span class="feature-tag">定时计划</span><span class="feature-tag">份量精控</span><span class="feature-tag">多餐设置</span></div>
      </div>
      <div class="feature-card">
        <div class="feature-icon">📡</div>
        <div class="feature-title">设备管理与监控</div>
        <div class="feature-text">支持多台设备绑定，按房间区域分组管理。实时监控设备在线状态、余粮量、电量及干燥剂。异常状态（堵料、缺粮、断网）自动推送告警。</div>
        <div class="feature-tags"><span class="feature-tag">多设备管理</span><span class="feature-tag">实时状态</span><span class="feature-tag">异常告警</span><span class="feature-tag">区域分组</span></div>
      </div>
      <div class="feature-card">
        <div class="feature-icon">💧</div>
        <div class="feature-title">干燥剂智能管理</div>
        <div class="feature-text">内置干燥剂模块，自动追踪剩余有效期。30 天智能倒计时提醒，确保宠物每一口粮食都新鲜干燥。告别手动记忆更换日期，让设备替你守护粮食品质。</div>
        <div class="feature-tags"><span class="feature-tag">30天倒计时</span><span class="feature-tag">智能提醒</span><span class="feature-tag">防潮保鲜</span><span class="feature-tag">一键重置</span></div>
      </div>
    </div>
  </div>
</section>

<!-- SPECS -->
<section class="specs-section">
  <div class="container">
    <div class="section-header">
      <div class="section-badge">🔧 技术规格</div>
      <h2 class="section-title">项目技术参数</h2>
      <p class="section-sub" style="color:rgba(249,250,251,0.5)">核心技术指标与实现细节。</p>
    </div>
    <div class="specs-grid">
      <div class="spec-card"><h4>支持平台</h4><div class="spec-value accent">iOS · Android · HarmonyOS</div></div>
      <div class="spec-card"><h4>开发方式</h4><div class="spec-value">混合 App</div></div>
      <div class="spec-card"><h4>设备通信</h4><div class="spec-value">Bluetooth LE + Wi-Fi 2.4GHz</div></div>
      <div class="spec-card"><h4>可调参数</h4><div class="spec-value accent">50+ 项</div></div>
      <div class="spec-card"><h4>软件版本</h4><div class="spec-value">v1.0.0</div></div>
      <div class="spec-card"><h4>语言支持</h4><div class="spec-value">中文 / English / Deutsch / Français / Italiano / Español / 日本語 / Русский</div></div>
      <div class="spec-card"><h4>数据记录维度</h4><div class="spec-value">喂食次数 / 摄入量 / 干燥剂状态</div></div>
      <div class="spec-card"><h4>告警类型</h4><div class="spec-value">6 种异常状态监控</div></div>
      <div class="spec-card"><h4>干燥剂管理</h4><div class="spec-value accent">30 天智能倒计时</div></div>
    </div>
  </div>
</section>

<!-- REVIEWS -->
<section class="reviews-section">
  <div class="container">
    <div class="section-header">
      <div class="section-badge">💬 用户反馈</div>
      <h2 class="section-title">用户真实评价</h2>
      <p class="section-sub">来自真实宠物主人的使用体验分享。</p>
    </div>
    <div class="reviews-grid">
      <div class="review-card">
        <div class="review-quote">"</div>
        <div class="review-stars">⭐⭐⭐⭐⭐</div>
        <div class="review-text">出差三天完全不用担心猫咪的饭了！定时计划设置好，每次喂完都能收到通知，安心多了。界面也很清爽，第一次用就上手了。</div>
        <div class="review-author">
          <div class="review-avatar">🐱</div>
          <div><div class="review-name">橘子的铲屎官</div><div class="review-role">养猫 3 年 · iOS 用户</div></div>
        </div>
      </div>
      <div class="review-card">
        <div class="review-quote">"</div>
        <div class="review-stars">⭐⭐⭐⭐⭐</div>
        <div class="review-text">家里两只狗，之前每次喂食都要专门回家。现在用这个 App 管理两台喂食机，余粮多少、设备是不是在线，一眼就能看到。告警也很及时。</div>
        <div class="review-author">
          <div class="review-avatar">🐶</div>
          <div><div class="review-name">豆豆爸爸</div><div class="review-role">养狗 5 年 · Android 用户</div></div>
        </div>
      </div>
      <div class="review-card">
        <div class="review-quote">"</div>
        <div class="review-stars">⭐⭐⭐⭐⭐</div>
        <div class="review-text">干燥剂管理功能超实用！之前总是忘记更换干燥剂，粮食受潮了都没注意。现在 App 会自动提醒，30 天倒计时一目了然。一键重置也很方便，省心多了。</div>
        <div class="review-author">
          <div class="review-avatar">😺</div>
          <div><div class="review-name">小胖的妈妈</div><div class="review-role">养猫 2 年 · 华为用户</div></div>
        </div>
      </div>
    </div>
  </div>
</section>
</div>

<!-- Page Summary -->
<section class="page-summary">
  <div class="page-summary__inner">
    <p class="page-summary__text fontRegular">{{ detailIntroduction }}</p>
    <div class="page-summary__preview">
      <img
        v-for="(img, index) in previewImages"
        :key="'preview-' + index"
        :src="img"
        alt=""
      />
    </div>
    <div class="page-summary__back fontRegular" @click="goBack">返回</div>
  </div>
</section>

<!-- Related Cases -->
<section class="related-cases">
  <div class="related-cases__inner">
    <div class="related-cases__title">
      <img :src="caseListText" alt="相关案例" />
    </div>
    <div class="related-cases__grid">
      <list-card
        v-for="(item, index) in relatedCaseList"
        :key="'related-case-' + index"
        class="noPadding"
        :src="item.image"
        :routeQuery="item.routeQuery"
        :keyIndex="index"
        :showDownName="false"
        :isRichHtml="true"
        :showFreeTrialTag="item.showFreeTrialTag"
        :backgroundColor="item.backgroundColor"
      />
    </div>
  </div>
</section>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import ListCard from "@/components/list-card/index.vue";
import { initRichHtmlReveal } from "../utils/useRichHtmlReveal";
import imageMap from "./images";

const getImage = (name) => imageMap[name] || "";

const onImageError = (event) => {
  const target = event.target;
  if (target) {
    target.style.display = "none";
  }
};

const introduceText = require("@/assets/images/introduce-text.png");
const caseListText = require("@/assets/images/case-list-text.png");

const projectIntroduction =
  "以宠物健康为核心，将智能硬件、定时喂食计划与干燥剂管理融为一体。无论你在哪里，随时掌控宠物的每一餐。DownyPaws 智能宠物喂食器采用混合 App 开发，覆盖 iOS、Android 和 HarmonyOS，通过蓝牙配网 + Wi-Fi 实现远程投喂与实时状态推送。";

const detailIntroduction =
  "云效科技崇尚创新，专注研发，拥有一支国内领先的技术团队，并凭借专业知识强、业务技术精、服务质量高、精神面貌佳等特点得到用户的一致赞誉。在软件开发的专业领域，云效科技与行业内诸多知名企业保持有长期稳定的合作关系。";

const previewImages = [
  require("@/assets/images/list-case/attract-investment/image-1.png"),
];

const relatedCaseList = [
  {
    image: require("@/assets/images/project-list-card/project-36.png"),
    backgroundColor: "#0FBFAE",
    routeQuery: { name: "intelligentElectricAdjustmentSystem", isRichHtml: true },
  },
  {
    image: require("@/assets/images/project-list-card/project-37.png"),
    backgroundColor: "#09C9B0",
    routeQuery: { name: "lovableHome", isRichHtml: true },
  },
  {
    image: require("@/assets/images/project-list-card/project-35.png"),
    backgroundColor: "#4459FE",
    routeQuery: { name: "channelAdvertisingFundManagementPlatform", isRichHtml: true },
  },
];

const router = useRouter();
const rootRef = ref(null);

const goBack = () => {
  router.go(-1);
};

onMounted(() => {
  initRichHtmlReveal(rootRef.value);
});
</script>

<style lang="scss" src="./case.scss"></style>
<style lang="scss" src="./layout.scss"></style>
